import { Dispatch, SetStateAction } from 'react';

import { Capability } from '@/hooks/useCapabilities';
import { ParameterInput } from '@/sections/attacks/capabilities/components/inputs';

import { OutputSettings } from './OutputSettings';

/**
 * Maps parameter names to the attack_plugins values that require them.
 * Parameters listed here are only visible when at least one of their
 * associated plugins is selected in the attack_plugins parameter.
 */
const PARAMETER_PLUGIN_DEPS: Record<string, string[]> = {
  c2_repo: ['github/interactive-shell', 'github/runner-on-runner'],
  target_os: ['github/runner-on-runner'],
  target_arch: ['github/runner-on-runner'],
  runner_labels: ['github/runner-on-runner'],
  delivery: ['github/ai-prompt-injection'],
  persistence_method: ['github/persistence'],
};

function isParameterVisible(
  paramName: string,
  parameterValues: Record<string, string>
): boolean {
  const deps = PARAMETER_PLUGIN_DEPS[paramName];
  if (!deps) return true; // No dependency — always visible

  const selectedPlugins = parameterValues['attack_plugins'];
  if (!selectedPlugins) return false; // Dependency exists but no plugins selected

  const selected = new Set(
    selectedPlugins
      .split(',')
      .map(s => s.trim())
      .filter(Boolean)
  );
  return deps.some(plugin => selected.has(plugin));
}

interface ParametersSectionProps {
  capability: Capability;
  parameterValues: Record<string, string>;
  setParameterValues: Dispatch<SetStateAction<Record<string, string>>>;
  validationErrors: Record<string, boolean>;
  setValidationErrors: Dispatch<SetStateAction<Record<string, boolean>>>;
  isPraetorianUser: boolean;
  largeArtifact: boolean;
  setLargeArtifact: (value: boolean) => void;
  onParameterBlur?: (paramName: string) => void;
}

/**
 * ParametersSection - Component for displaying and editing capability parameters
 *
 * Displays configurable parameters for a capability, with different views
 * for Praetorian users (editable) and customers (read-only).
 */
export function ParametersSection({
  capability,
  parameterValues,
  setParameterValues,
  validationErrors,
  setValidationErrors,
  isPraetorianUser,
  largeArtifact,
  setLargeArtifact,
  onParameterBlur,
}: ParametersSectionProps) {
  // Handler for parameter value changes
  const handleParameterChange = (paramName: string, value: string) => {
    setParameterValues(prev => ({
      ...prev,
      [paramName]: value,
    }));
    // Clear validation error when user starts typing
    if (validationErrors[paramName]) {
      setValidationErrors(prev => ({
        ...prev,
        [paramName]: false,
      }));
    }
  };

  if (!capability.parameters || capability.parameters.length === 0) {
    return (
      <div className="h-fit">
        {/* Output Settings - Only show for capabilities that support big output */}
        {isPraetorianUser && capability.largeArtifact && (
          <OutputSettings
            largeArtifact={largeArtifact}
            setLargeArtifact={setLargeArtifact}
          />
        )}
        <div className="py-8 text-center">
          <p className="text-chariot-text-secondary">
            This capability has no configurable parameters.
          </p>
        </div>
      </div>
    );
  }

  // Separate required and optional parameters, filtering by plugin visibility
  const visibleParams = (capability.parameters || []).filter(param =>
    isParameterVisible(param.name, parameterValues)
  );
  const requiredParams = visibleParams.filter(param => param.required === true);
  const optionalParams = visibleParams.filter(
    param => param.required === false || param.required === undefined
  );

  return (
    <div className="h-fit">
      {/* Required Parameters */}
      {requiredParams.length > 0 && (
        <div className="mb-8">
          <div className="flex flex-col gap-3">
            {requiredParams.map((param, index) => (
              <ParameterInput
                key={`required-${index}`}
                param={param}
                value={parameterValues[param.name] || ''}
                onChange={value => handleParameterChange(param.name, value)}
                onBlur={onParameterBlur}
                error={
                  validationErrors[param.name]
                    ? `${param.name} is required`
                    : undefined
                }
                readOnly={!isPraetorianUser}
                isRequired={true}
              />
            ))}
          </div>
        </div>
      )}

      {/* Output Settings - Only show for capabilities that support big output */}
      {isPraetorianUser && capability.largeArtifact && (
        <OutputSettings
          largeArtifact={largeArtifact}
          setLargeArtifact={setLargeArtifact}
        />
      )}

      {/* Optional Parameters */}
      {optionalParams.length > 0 && (
        <div>
          <h4 className="text-md text-chariot-text-primary mb-4 font-medium">
            Optional Parameters
          </h4>
          <div className="flex flex-col gap-3">
            {optionalParams.map((param, index) => (
              <ParameterInput
                key={`optional-${index}`}
                param={param}
                value={parameterValues[param.name] || ''}
                onChange={value => handleParameterChange(param.name, value)}
                readOnly={!isPraetorianUser}
                isRequired={false}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
